import React, { Component, ErrorInfo, ReactNode } from 'react';
import logger from '../services/Logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary Component
 * Cattura errori JavaScript in tutto l'albero dei componenti figli
 * Previene crash dell'intera applicazione
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Aggiorna lo state per mostrare la UI di fallback
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log dell'errore per monitoring
    logger.error('ErrorBoundary caught an error:', {
      error: error.toString(),
      errorInfo: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    // In produzione, invierebbe a servizio di monitoring (es. Sentry)
    if (process.env.NODE_ENV === 'production') {
      // TODO: Implementare invio a Sentry o altro servizio
      // sendErrorToMonitoringService(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // UI di fallback personalizzata
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI di fallback predefinita
      return (
        <div style={{
          padding: '50px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          margin: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>
            ⚠️ Si è verificato un errore
          </h2>
          <p style={{ color: '#6c757d', marginBottom: '30px' }}>
            Ci scusiamo per l'inconveniente. Si è verificato un errore imprevisto.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 30px',
              backgroundColor: 'var(--primary-color, #229a61)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Ricarica la pagina
          </button>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '30px', textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', color: '#6c757d' }}>
                Dettagli errore (solo sviluppo)
              </summary>
              <pre style={{
                backgroundColor: '#f1f1f1',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
                marginTop: '10px'
              }}>
                {this.state.error.toString()}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;